import { Fragment, useState } from 'react';

import Button from '@components/button/Button';
import Chip from '@components/chip/Chip';
import Icon from '@components/icon/Icon';
import TagInput from '@components/tag-input/TagInput';
import TAG_COLOR_OPTIONS from '@constants/tagColors';
import { isUserAssignable } from '@helpers/tags';
import Modal from '@layouts/modal/Modal';
import { Tag } from 'types/lead-management';

import {
  MAX_INPUT_LENGTH,
  MIN_INPUT_LENGTH as MIN_TAG_INPUT_LENGTH
} from './constants';
import EditTagPopover from './EditTagPopover';
import { getTagChipStyle } from './ReadOnlyTagChip';
import TagOverflowChip from './TagOverflowChip';

const DEFAULT_NEW_TAG_COLOR = '#F2E1E9';
const MAX_VISIBLE_CHIPS = 2; // the rest go in the "+N" tooltip
const DISCONTINUED_KEY = 'DISCONTINUED';
const GROUP_CUSTOM = 'Custom tags';
const GROUP_DEFAULT = 'Default tags';

/* ------------------------------ tag helpers ------------------------------ */

// New tags typed by the admin get a temporary id from TagInput until the API creates them.
const isTempTag = (t: Tag) => String(t.id).startsWith('custom-');

/* -------------------------------- component ------------------------------- */

interface MemberTagFieldProps {
  selectedTags: Tag[];
  tagOptions: Tag[];
  onSave: (tags: Tag[]) => void;
  onCreateTag: (name: string, color: string) => Promise<Tag | undefined>;
  onUpdateTag: (tag: Tag) => Promise<Tag | undefined>;
  onTagUpdated?: (oldId: string, updated: Tag) => void;
  canManageTags: boolean;
  onConfirmOpenChange?: (isOpen: boolean) => void;
  disabled?: boolean;
}

/**
 * Tag field for the Add member / Edit member modals.
 * Render it with a `key` (e.g. the member id) so it starts fresh for each member.
 */
const MemberTagField = ({
  selectedTags,
  tagOptions,
  onSave,
  onCreateTag,
  onUpdateTag,
  onTagUpdated,
  canManageTags,
  onConfirmOpenChange,
  disabled
}: MemberTagFieldProps) => {
  const [inputValue, setInputValue] = useState('');
  const [pendingDiscontinued, setPendingDiscontinued] = useState<Tag[] | null>(
    null
  );

  const nonEditableNames = tagOptions
    .filter((t) => t.is_editable === false)
    .map((t) => t.name);

  // Member tags (from the member API) only carry id/name/colour/type/source.
  // Merge them with the branch tag option so read-only rules still apply.
  const tagOptionsById = new Map(tagOptions.map((t) => [String(t.id), t]));
  const withOption = (t: Tag) => tagOptionsById.get(String(t.id)) ?? t;
  // Tags the system applied to this member (e.g. Closed from a payment) can't be removed.
  // Branch options carry no `source`, so read it from the member's own tag.
  const isSystemApplied = (t: Tag) =>
    selectedTags.some((s) => s.id === t.id && s.source === 'system');
  const isUnassignableTag = (t: Tag) => !isUserAssignable(withOption(t));
  const isReadOnlyTag = (t: Tag) => isUnassignableTag(t) || isSystemApplied(t);
  const isDiscontinuedTag = (t: Tag) =>
    withOption(t).system_key === DISCONTINUED_KEY;

  // Within each group: selected before unselected, then assignable before not
  const selectedIds = new Set(selectedTags.map((t) => String(t.id)));
  const getSortRank = (t: Tag) =>
    (selectedIds.has(String(t.id)) ? 0 : 2) + (isUnassignableTag(t) ? 1 : 0);
  const sortedTagOptions = [...tagOptions].sort(
    (a, b) => getSortRank(a) - getSortRank(b)
  );

  /* ----------------------------- selection logic ---------------------------- */

  const handleTagsChange = async (tags: Tag[]) => {
    const added = tags.filter((t) => !selectedTags.some((d) => d.id === t.id));
    const removed = selectedTags.filter(
      (d) => !tags.some((t) => t.id === d.id)
    );

    // Read-only tags can't be added or removed from the UI.
    if ([...added, ...removed].some(isReadOnlyTag)) return;

    // "+ Create": the tag is created in the branch, then selected.
    const newTag = added.find(isTempTag);
    if (newTag) {
      // Enter in the search box can still try to create one when Create is hidden
      if (!canManageTags) return;
      const created = await onCreateTag(
        newTag.name,
        newTag.color || DEFAULT_NEW_TAG_COLOR
      );
      if (created) {
        onSave([...selectedTags.filter((t) => !isTempTag(t)), created]);
      }
      return;
    }

    // Discontinued needs a confirmation the first time it is added.
    if (
      added.some(isDiscontinuedTag) &&
      !selectedTags.some(isDiscontinuedTag)
    ) {
      setPendingDiscontinued(tags); // hold it; it is not selected until Confirm
      onConfirmOpenChange?.(true);
      return;
    }

    onSave(tags);
  };

  const handleUpdateTag = async (tag: Tag) => {
    if (!canManageTags) return false;
    const updated = await onUpdateTag(tag);
    if (!updated) return false;
    // A first edit of a default custom tag returns a NEW id: swap it everywhere.
    onTagUpdated?.(tag.id, updated);
    return true;
  };

  const confirmDiscontinued = () => {
    if (pendingDiscontinued) onSave(pendingDiscontinued);
    setPendingDiscontinued(null);
    onConfirmOpenChange?.(false);
  };
  const backToEditing = () => {
    setPendingDiscontinued(null);
    onConfirmOpenChange?.(false);
  };

  /* --------------------------------- render --------------------------------- */

  const renderTagChip = (tag: Tag, onRemove: (e: React.MouseEvent) => void) => (
    <Chip
      label={tag.name}
      className="!rounded-xl px-2 py-1 text-sm"
      style={getTagChipStyle(tag)}
      labelStyle="!text-sm !font-normal"
      size="16"
      onRemove={isReadOnlyTag(tag) ? undefined : onRemove} // no ✕ on read-only tags
    />
  );

  // ✕ on a chip inside the "+N" tooltip; the tooltip sits inside the field, so stop the
  // click from also toggling the popup
  const removeHiddenTag = (tag: Tag) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handleTagsChange(selectedTags.filter((t) => t.id !== tag.id));
  };

  // Called once per selected tag: the first few render as chips, the last visible one is
  // followed by the "+N" chip, and the rest render nothing
  const orderedSelectedTags = [...selectedTags].reverse();
  const renderChip = (tag: Tag, onRemove: (e: React.MouseEvent) => void) => {
    const index = orderedSelectedTags.findIndex((t) => t.id === tag.id);

    if (index >= MAX_VISIBLE_CHIPS) return null;

    const isLastVisible = index === MAX_VISIBLE_CHIPS - 1;

    return (
      <Fragment key={tag.id}>
        {renderTagChip(tag, onRemove)}
        {isLastVisible && (
          <TagOverflowChip
            tags={orderedSelectedTags.slice(MAX_VISIBLE_CHIPS)}
            renderTag={(hidden) =>
              renderTagChip(hidden, removeHiddenTag(hidden))
            }
          />
        )}
      </Fragment>
    );
  };

  return (
    <>
      <TagInput
        value={inputValue}
        onChange={setInputValue}
        onTagsChange={handleTagsChange}
        selectedTags={selectedTags}
        tagOptions={sortedTagOptions}
        onUpdateTag={handleUpdateTag}
        renderChip={renderChip}
        disabled={disabled}
        keepOpenOnSelect
        // Typing only serves search and create; without tag management, pick from the list
        searchable={canManageTags}
        placeholder={canManageTags ? undefined : 'Select Tag'}
        nameLengthRange={[MIN_TAG_INPUT_LENGTH, MAX_INPUT_LENGTH]}
        selectorProps={{
          positionFixed: true,
          className: '!max-h-none',
          itemListClassName: '!max-h-72',
          showItemDividers: false,
          groupBy: (t: Tag) =>
            t.type === 'system' ? GROUP_DEFAULT : GROUP_CUSTOM,
          groupOrder: [GROUP_CUSTOM, GROUP_DEFAULT],
          nonEditableFields: nonEditableNames,
          showEditButton: canManageTags,
          ...(!canManageTags && { createButtonClassName: '!hidden' }),
          isItemDisabled: isReadOnlyTag,
          // Not user-assignable: greyed out and disabled, but still shows if selected
          isItemMuted: isUnassignableTag,
          hideCreateWhenExists: true,
          EditModalComponent: EditTagPopover,
          createColorOptions: TAG_COLOR_OPTIONS.map((c) => c.bgColor)
        }}
      />

      <Modal
        isOpen={!!pendingDiscontinued}
        onCancel={backToEditing}
        isCloseIconRequired={false}
        className="max-h-[340px] w-[450px] max-w-[90vw] !rounded-xl"
      >
        {/* data-edit-modal stops ItemSelector's click-outside from closing the dropdown */}
        <div data-edit-modal className="flex flex-col gap-6 p-6">
          <Icon
            name="red-tag-with-rounded-bg"
            size="none"
            className="mx-auto size-18"
          />
          <div className="flex flex-col gap-3 text-base text-darkGrey">
            <div className="text-center text-base font-extrabold text-darkGrey">
              Add Discontinued tag?
            </div>
            <div className="text-base text-darkGrey">
              Since the Discontinued tag was added, we will disable the pending
              payment links for this member. This action cannot be undone.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="tertiary"
              label="Back to editing"
              size="medium"
              handleClick={backToEditing}
              className="h-11 flex-1 text-sm font-medium"
            />
            <Button
              variant="primary"
              label="Confirm"
              size="medium"
              handleClick={confirmDiscontinued}
              className="h-11 flex-1 text-sm font-medium"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default MemberTagField;
